import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { ZoomIn, ZoomOut, Maximize2, Volume2, VolumeX } from "lucide-react";
import Monolith from "./Monolith";
import "./space-v3.css";

const CLUSTER_LAYOUT = [
  { x: -390, y: -230, rot: -3, depth: 82 },
  { x: 365, y: -205, rot: 2, depth: 58 },
  { x: -455, y: 5, rot: -2, depth: 40 },
  { x: 430, y: 20, rot: 2, depth: 95 },
  { x: -360, y: 245, rot: 2, depth: 70 },
  { x: 350, y: 245, rot: -2, depth: 52 },
  { x: -90, y: 330, rot: 1, depth: 108 },
  { x: 160, y: -330, rot: -1, depth: 35 },
];

const MANIFEST_OFFSETS = [
  { x: 150, y: 62, rot: 3 },
  { x: 120, y: -72, rot: -4 },
  { x: -132, y: 68, rot: 2 },
  { x: -118, y: -74, rot: -2 },
];

function polar(i, n, radius) {
  const a = (i / Math.max(1, n)) * Math.PI * 2 - Math.PI / 2;
  return { x: Math.cos(a) * radius, y: Math.sin(a) * radius };
}

export default function SpaceStage({ graph, onSelectCluster, onSelectManifestation, pulse }) {
  const wrapRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, scale: 0.78, rot: 0 });
  const drag = useRef({ active: false, startX: 0, startY: 0, ox: 0, oy: 0, moved: false });
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const [sound, setSound] = useState(false);
  const audioRef = useRef(null);
  const [ripple, setRipple] = useState(0);
  const [pulseId, setPulseId] = useState(null);

  const clusters = graph?.clusters || [];
  const manifestations = graph?.manifestations || [];

  useEffect(() => {
    const tablet = window.innerWidth < 1100;
    const mobile = window.innerWidth < 700;
    setView((v) => ({ ...v, scale: mobile ? 0.42 : tablet ? 0.56 : 0.78 }));
  }, []);

  useEffect(() => {
    if (!pulse) return;
    setRipple((r) => r + 1);
    setPulseId(pulse.clusterId || null);
    const t = setTimeout(() => setPulseId(null), 2600);
    return () => clearTimeout(t);
  }, [pulse]);

  const clusterPositions = useMemo(
    () => clusters.map((c, i) => {
      const fallback = polar(i, clusters.length, 380);
      const p = CLUSTER_LAYOUT[i] || { ...fallback, rot: i % 2 ? 2 : -2, depth: 45 + (i % 4) * 18 };
      return { ...c, ...p };
    }),
    [clusters]
  );

  const shardPositions = useMemo(() => {
    const usage = {};
    return manifestations.map((m, i) => {
      const clusterId = m.cluster_id || m.source_cluster_id || m.cluster?.id;
      const parent = clusterPositions.find((c) => c.id === clusterId);
      if (parent) {
        const slot = usage[parent.id] || 0;
        usage[parent.id] = slot + 1;
        const off = MANIFEST_OFFSETS[slot % MANIFEST_OFFSETS.length];
        return { ...m, x: parent.x + off.x, y: parent.y + off.y, rot: off.rot, parentX: parent.x, parentY: parent.y };
      }
      const p = polar(i + 0.45, Math.max(1, manifestations.length), 520);
      return { ...m, ...p, rot: i % 2 ? 3 : -3, parentX: 0, parentY: 0 };
    });
  }, [manifestations, clusterPositions]);

  const clampScale = (s) => Math.min(1.65, Math.max(0.3, s));

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      drag.current = { active: true, startX: e.clientX, startY: e.clientY, ox: view.x, oy: view.y, moved: false };
    }
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = {
        distance: Math.hypot(b.x - a.x, b.y - a.y),
        angle: Math.atan2(b.y - a.y, b.x - a.x),
        scale: view.scale,
        rot: view.rot,
      };
      drag.current.active = false;
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const distance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      const angle = Math.atan2(b.y - a.y, b.x - a.x);
      const scale = clampScale(pinch.current.scale * (distance / pinch.current.distance));
      const rot = pinch.current.rot + ((angle - pinch.current.angle) * 180) / Math.PI * 0.2;
      setView((v) => ({ ...v, scale, rot }));
      return;
    }
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) drag.current.moved = true;
    setView((v) => ({ ...v, x: drag.current.ox + dx, y: drag.current.oy + dy }));
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) drag.current.active = false;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    setView((v) => ({ ...v, scale: clampScale(v.scale + delta) }));
  };

  const zoom = (dir) => setView((v) => ({ ...v, scale: clampScale(v.scale + dir * 0.15) }));
  const reset = () => setView({ x: 0, y: 0, scale: window.innerWidth < 700 ? 0.42 : window.innerWidth < 1100 ? 0.56 : 0.78, rot: 0 });

  const toggleSound = useCallback(() => {
    setSound((s) => {
      const next = !s;
      try {
        if (next) {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = 54;
          gain.gain.value = 0.025;
          osc.connect(gain).connect(ctx.destination);
          osc.start();
          audioRef.current = { ctx, osc };
        } else if (audioRef.current) {
          audioRef.current.osc.stop();
          audioRef.current.ctx.close();
          audioRef.current = null;
        }
      } catch {}
      return next;
    });
  }, []);

  const worldTransform = `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale}) rotate(${view.rot}deg)`;
  const tension = graph?.stats?.tension || 0.3;

  return (
    <div
      ref={wrapRef}
      data-testid="cognitive-space-canvas-wrapper"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={(e) => e.pointerType === "mouse" && onPointerUp(e)}
      onWheel={onWheel}
      className="cs-stage"
      style={{ cursor: drag.current.active ? "grabbing" : "grab" }}
    >
      <div className="cs-depth-haze" />
      <div className="cs-stage-grid" />
      <div className="cs-vignette" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 5 }} />

      <div className="cs-world" style={{ transform: worldTransform, transition: pointers.current.size ? "none" : "transform 120ms ease-out" }}>
        <svg width="1400" height="1200" viewBox="0 0 1400 1200" className="cs-connection-map">
          {clusterPositions.map((c) => (
            <g key={`l-${c.id}`}>
              <line x1="700" y1="600" x2={700 + c.x} y2={600 + c.y} stroke={pulseId === c.id ? "rgba(255,46,120,.82)" : "rgba(224,218,205,.24)"} strokeWidth={pulseId === c.id ? 1.7 : 0.8} />
              <circle cx={700 + c.x} cy={600 + c.y} r="3" fill={pulseId === c.id ? "#ff2e78" : "#d8d2c6"} opacity=".7" />
            </g>
          ))}
          {shardPositions.map((m) => (
            <line key={`ls-${m.id}`} x1={700 + m.parentX} y1={600 + m.parentY} x2={700 + m.x} y2={600 + m.y} stroke="rgba(255,46,120,.32)" strokeWidth="0.7" strokeDasharray="2 6" />
          ))}
        </svg>

        {ripple > 0 && <div key={ripple} className="cs-space-ripple" />}
        <Monolith tension={tension} />

        {clusterPositions.map((c, i) => {
          const active = pulseId === c.id;
          const manifestationCount = c.manifestation_count ?? manifestations.filter((m) => (m.cluster_id || m.source_cluster_id || m.cluster?.id) === c.id).length;
          return (
            <button
              key={c.id}
              data-testid={`cluster-node-${c.id}`}
              onClick={() => !drag.current.moved && onSelectCluster(c)}
              className={`cs-cluster-card ${active ? "is-active" : ""}`}
              style={{ left: c.x, top: c.y, "--card-depth": `${c.depth}px`, "--card-rotation": `${c.rot}deg`, "--card-delay": `${i * -0.45}s` }}
            >
              <span className="cs-cluster-kicker">SEMANTISCHES CLUSTER</span>
              <strong>{c.name}</strong>
              <span className="cs-cluster-rule" />
              <span className="cs-cluster-meta"><span>AKTIVITÄT</span><i /></span>
              <span className="cs-cluster-count">{manifestationCount || 0} MANIFESTATIONEN</span>
            </button>
          );
        })}

        {shardPositions.map((m, i) => (
          <button
            key={m.id}
            data-testid={`manifestation-shard-${m.id}`}
            onClick={() => !drag.current.moved && onSelectManifestation(m)}
            className="cs-manifest-card"
            style={{ left: m.x, top: m.y, "--manifest-depth": `${120 + (i % 3) * 24}px`, "--manifest-rotation": `${m.rot || 0}deg`, "--manifest-delay": `${i * -0.7}s` }}
            title={m.title}
          >
            {m.image ? <img src={m.image} alt="" draggable="false" /> : <span className="cs-manifest-placeholder" />}
            <span className="cs-manifest-plus">+</span>
          </button>
        ))}
      </div>

      <div data-testid="space-tension-meter" className="cs-tension-panel">
        <div className="cs-mono" style={{ fontSize: 9, color: "var(--cs-mute)" }}>RAUM-SPANNUNG</div>
        <div className="cs-tension-track"><div style={{ width: `${Math.min(100, tension * 100)}%` }} /></div>
        <div className="cs-mono cs-tension-stats">{graph?.stats?.accepted_contributions || 0} GEDANKEN · {clusters.length} CLUSTER · {manifestations.length} MANIFESTATIONEN</div>
      </div>

      <div className="cs-stage-controls">
        {[
          { icon: ZoomIn, fn: () => zoom(1), tid: "space-zoom-in-btn" },
          { icon: ZoomOut, fn: () => zoom(-1), tid: "space-zoom-out-btn" },
          { icon: Maximize2, fn: reset, tid: "space-reset-view-btn" },
          { icon: sound ? Volume2 : VolumeX, fn: toggleSound, tid: "space-sound-toggle-btn" },
        ].map(({ icon: Icon, fn, tid }) => (
          <button key={tid} data-testid={tid} onClick={fn} className="cs-control-btn"><Icon size={16} /></button>
        ))}
      </div>
    </div>
  );
}
